import React, { useState } from 'react';
import { Filter, X, Search } from 'lucide-react';

// Style constants
const COLORS = {
  NEUTRAL_BG: '#F8F9FA',
  NEUTRAL_WHITE: '#FFFFFF',
  NEUTRAL_TEXT: '#1A1A1A',
  ACCENT_GRAY: '#6B7280',
  PRIMARY_BROWN: '#8B4513',
  ERROR_RED: '#DC2626',
  SUCCESS_GREEN: '#059669'
};

const FONTS = {
  heading: { fontFamily: 'Inter, system-ui, sans-serif' },
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export function ProductFilters({ 
  filters, 
  onUpdateFilters, 
  onClearFilters, 
  availableCategories, 
  availableSubCategories,
  activeFilterCount = 0,
  taxonomyData = {}
}) {
  const [showModal, setShowModal] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  const statusOptions = [
    { id: 'all', name: 'All Products' },
    { id: 'active', name: 'Active' },
    { id: 'inactive', name: 'Inactive' }
  ];

  const sortOptions = [
    { id: 'name', name: 'Name A-Z' },
    { id: 'name-desc', name: 'Name Z-A' },
    { id: 'price', name: 'Price Low-High' },
    { id: 'price-desc', name: 'Price High-Low' },
    { id: 'date', name: 'Date Created' }
  ];

  const handleApplyFilters = () => {
    onUpdateFilters(localFilters);
    setShowModal(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: 'all',
      category: '',
      subCategory: '',
      search: ''
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
    setShowModal(false);
  };

  const handleCancel = () => {
    setLocalFilters(filters);
    setShowModal(false);
  };

  const updateLocalFilter = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <>
      {/* Search Bar */}
      <div style={{ 
        background: COLORS.NEUTRAL_WHITE, 
        padding: '12px 16px',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {/* Search Input */}
          <div style={{ 
            flex: 1, 
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Search 
              size={20} 
              style={{ 
                position: 'absolute', 
                left: 12, 
                zIndex: 1,
                color: COLORS.ACCENT_GRAY
              }}
            />
            <input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={(e) => onUpdateFilters({ search: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 8px 8px 40px',
                border: '1px solid #E0E0E0',
                borderRadius: 8,
                fontSize: 12,
                background: COLORS.NEUTRAL_BG,
                ...FONTS.body
              }}
              aria-label="Search products"
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: COLORS.NEUTRAL_BG,
              border: '1px solid #E0E0E0',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 100,
              ...FONTS.body
            }}
            aria-label={`Filter products${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
          >
            <Filter size={16} style={{ color: COLORS.ACCENT_GRAY }} />
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancel();
            }
          }}
        >
          <div 
            style={{
              background: COLORS.NEUTRAL_WHITE,
              borderRadius: 12,
              padding: '16px',
              maxWidth: 400,
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: 18,
                fontWeight: 700,
                color: COLORS.NEUTRAL_TEXT,
                margin: 0,
                ...FONTS.heading
              }}>
                Filter Products
              </h3>
              <button
                onClick={handleCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: COLORS.ACCENT_GRAY,
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Close filter modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* Status Filter Section */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '12px',
                ...FONTS.body
              }}>
                Status
              </h4>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {statusOptions.map(option => (
                  <label
                    key={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                      background: localFilters.status === option.id ? COLORS.PRIMARY_BROWN : COLORS.NEUTRAL_WHITE,
                      color: localFilters.status === option.id ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      ...FONTS.body
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={option.id}
                      checked={localFilters.status === option.id}
                      onChange={(e) => updateLocalFilter('status', e.target.value)}
                      style={{ marginRight: 8 }}
                    />
                    {option.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Listing Type Filter Section */}
            {taxonomyData.listingType && taxonomyData.listingType.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Listing Type
                </h4>
                <select
                  value={localFilters.listingType || ''}
                  onChange={(e) => updateLocalFilter('listingType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Listing Types</option>
                  {taxonomyData.listingType
                    .sort((a, b) => a.display.localeCompare(b.display))
                    .map(item => (
                      <option key={item.value} value={item.value}>
                        {item.display}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Item Category Filter Section */}
            {taxonomyData.itemCategory && taxonomyData.itemCategory.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Item Category
                </h4>
                <select
                  value={localFilters.itemCategory || ''}
                  onChange={(e) => updateLocalFilter('itemCategory', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Item Categories</option>
                  {taxonomyData.itemCategory
                    .sort((a, b) => a.display.localeCompare(b.display))
                    .map(item => (
                      <option key={item.value} value={item.value}>
                        {item.display}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Cuisine Filter Section */}
            {taxonomyData.cuisine && taxonomyData.cuisine.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Cuisine
                </h4>
                <select
                  value={localFilters.cuisine || ''}
                  onChange={(e) => updateLocalFilter('cuisine', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Cuisines</option>
                  {taxonomyData.cuisine
                    .sort((a, b) => a.display.localeCompare(b.display))
                    .map(item => (
                      <option key={item.value} value={item.value}>
                        {item.display}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Course Type Filter Section */}
            {taxonomyData.courseType && taxonomyData.courseType.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Course Type
                </h4>
                <select
                  value={localFilters.courseType || ''}
                  onChange={(e) => updateLocalFilter('courseType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Course Types</option>
                  {taxonomyData.courseType
                    .sort((a, b) => a.display.localeCompare(b.display))
                    .map(item => (
                      <option key={item.value} value={item.value}>
                        {item.display}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Dish Type Filter Section */}
            {taxonomyData.dishType && taxonomyData.dishType.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Dish Type
                </h4>
                <select
                  value={localFilters.dishType || ''}
                  onChange={(e) => updateLocalFilter('dishType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Dish Types</option>
                  {taxonomyData.dishType
                    .sort((a, b) => a.display.localeCompare(b.display))
                    .map(item => (
                      <option key={item.value} value={item.value}>
                        {item.display}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Subcategory Filter Section */}
            {availableSubCategories.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  ...FONTS.body
                }}>
                  Subcategory
                </h4>
                <select
                  value={localFilters.subCategory}
                  onChange={(e) => updateLocalFilter('subCategory', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 12,
                    background: COLORS.NEUTRAL_WHITE,
                    ...FONTS.body
                  }}
                >
                  <option value="">All Subcategories</option>
                  {availableSubCategories.map(subCategory => (
                    <option key={subCategory} value={subCategory}>
                      {subCategory}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              borderTop: '1px solid #E0E0E0',
              paddingTop: '20px'
            }}>
              <button
                onClick={handleClearFilters}
                style={{
                  flex: 1,
                  background: COLORS.NEUTRAL_BG,
                  color: COLORS.NEUTRAL_TEXT,
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  ...FONTS.body
                }}
              >
                <X size={16} />
                Clear All
              </button>
              <button
                onClick={handleApplyFilters}
                style={{
                  flex: 1,
                  background: COLORS.PRIMARY_BROWN,
                  color: COLORS.NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  ...FONTS.body
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
